import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AccountBalanceWallet as WalletIcon,
  ChevronRight as ChevronRightIcon,
  Clear as ClearIcon,
  Edit as EditIcon,
  FileDownload as FileDownloadIcon,
  Group as GroupIcon,
  Inventory2 as InventoryIcon,
  Search as SearchIcon,
  Send as SendIcon,
  TaskAlt as TaskAltIcon,
} from '@mui/icons-material';
import {
  exportConsignorDirectory,
  fetchConsignorDirectory,
  resendConsignorOnboarding,
} from '../api/consignorDirectory';
import type {
  ConsignorDirectorySummary,
  ConsignorOrderBy,
  ConsignorOrderDirection,
  ConsignorRow,
} from '../types/consignorDirectory';
import { ADMIN_INVENTORY_PATH } from '../routes/routePaths';
import AdminEditConsignorModal from './AdminEditConsignorModal';
import InventoryStatusChip from './InventoryStatusChip';

// ─── Constants ────────────────────────────────────────────────────────────────

const CARD_BORDER = '1px solid';
const CARD_BORDER_COLOR = 'rgba(226, 232, 240, 1)';
const TABLE_HEADER_BG = 'rgba(248, 250, 252, 1)';

const moneyMx = (n: number) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n);

// ─── Avatar ───────────────────────────────────────────────────────────────────

const AVATAR_PALETTE = [
  '#111827',
  '#1D4ED8',
  '#0F766E',
  '#7C3AED',
  '#B45309',
  '#BE185D',
  '#0369A1',
  '#15803D',
];

function avatarColor(name: string): string {
  const code = (name.charCodeAt(0) || 0) + (name.charCodeAt(1) || 0);
  return AVATAR_PALETTE[code % AVATAR_PALETTE.length];
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function ConsignorAvatar({ name }: { name: string }): React.ReactElement {
  return (
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: '50%',
        bgcolor: avatarColor(name),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        color: 'white',
        fontWeight: 800,
        fontSize: 13,
        letterSpacing: 0.5,
        userSelect: 'none',
      }}
    >
      {initials(name)}
    </Box>
  );
}

// ─── Summary cards ────────────────────────────────────────────────────────────

interface MetricCardProps {
  label: string;
  value: string;
  icon: React.ReactNode;
  iconBg: string;
  iconColor: string;
  sub?: string;
}

function MetricCard({
  label,
  value,
  icon,
  iconBg,
  iconColor,
  sub,
}: MetricCardProps): React.ReactElement {
  return (
    <Card
      sx={{
        p: 2.5,
        border: CARD_BORDER,
        borderColor: CARD_BORDER_COLOR,
        boxShadow: 'none',
        borderRadius: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: '50%',
          bgcolor: iconBg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: iconColor,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="h5" fontWeight={800} sx={{ lineHeight: 1.2 }}>
          {value}
        </Typography>
        {sub && (
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.25 }}>
            {sub}
          </Typography>
        )}
      </Box>
    </Card>
  );
}

MetricCard.defaultProps = { sub: undefined };

// ─── Sort label helper ────────────────────────────────────────────────────────

const sortLabelSx = {
  '& .MuiTableSortLabel-icon': { opacity: 0.45 },
  '&.Mui-active .MuiTableSortLabel-icon': { opacity: 1 },
};

// ─── Main page ────────────────────────────────────────────────────────────────

export default function AdminConsignorsPage(): React.ReactElement {
  const navigate = useNavigate();

  const [rows, setRows] = useState<ConsignorRow[]>([]);
  const [summary, setSummary] = useState<ConsignorDirectorySummary | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [orderBy, setOrderBy] = useState<ConsignorOrderBy | undefined>(undefined);
  const [orderDirection, setOrderDirection] = useState<ConsignorOrderDirection | undefined>(
    undefined
  );
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [editConsignorId, setEditConsignorId] = useState<number | null>(null);
  const [resendingId, setResendingId] = useState<number | null>(null);
  const [snack, setSnack] = useState<{ ok: boolean; message: string } | null>(null);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    const result = await fetchConsignorDirectory({
      page: page + 1,
      pageSize,
      search: search || undefined,
      orderBy,
      orderDirection,
    });
    setLoading(false);
    if (!result.ok) {
      setLoadError(result.error);
      setRows([]);
      setTotal(0);
      return;
    }
    setRows(result.data.items);
    setTotal(result.data.total);
    if (result.data.summary) setSummary(result.data.summary);
  }, [page, pageSize, search, orderBy, orderDirection]);

  useEffect(() => {
    load().catch(() => {});
  }, [load]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setSearchInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch(value);
      setPage(0);
    }, 400);
  }, []);

  const handleSort = useCallback(
    (field: ConsignorOrderBy) => {
      if (orderBy === field) {
        setOrderDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
      } else {
        setOrderBy(field);
        setOrderDirection(field === 'id' ? 'asc' : 'desc');
      }
      setPage(0);
    },
    [orderBy]
  );

  const handleRowClick = useCallback(
    (id: number) => {
      navigate(`${ADMIN_INVENTORY_PATH}?consignorId=${id}`);
    },
    [navigate]
  );

  const handleExport = useCallback(async () => {
    setExportLoading(true);
    await exportConsignorDirectory({ search: search || undefined, orderBy, orderDirection });
    setExportLoading(false);
  }, [search, orderBy, orderDirection]);

  const handleResendOnboarding = useCallback(async (id: number) => {
    setResendingId(id);
    const result = await resendConsignorOnboarding(id);
    setResendingId(null);
    setSnack(
      result.ok
        ? { ok: true, message: 'WhatsApp de registro enviado.' }
        : { ok: false, message: result.error }
    );
  }, []);

  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(e.target.value, 10));
    setPage(0);
  };

  const rangeLabel =
    total === 0 ? '0' : `${page * pageSize + 1}–${Math.min((page + 1) * pageSize, total)}`;

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
      {/* ── Header ── */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { sm: 'flex-end' },
          justifyContent: 'space-between',
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{ fontWeight: 700, letterSpacing: '-0.02em', textAlign: 'left' }}
          >
            Consignadores
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, textAlign: 'left' }}>
            Directorio de todos los socios de consignación.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={
            exportLoading ? <CircularProgress size={16} color="inherit" /> : <FileDownloadIcon />
          }
          onClick={() => {
            handleExport().catch(() => {});
          }}
          disabled={exportLoading}
          sx={{
            borderColor: CARD_BORDER_COLOR,
            color: 'text.primary',
            fontWeight: 600,
            textTransform: 'none',
            whiteSpace: 'nowrap',
          }}
        >
          {exportLoading ? 'Exportando…' : 'Descargar CSV'}
        </Button>
      </Box>

      {/* ── Summary cards ── */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
          gap: 2.5,
          mb: 3,
        }}
      >
        <MetricCard
          label="Total consignadores"
          value={summary ? String(summary.totalConsignors) : '—'}
          icon={<GroupIcon />}
          iconBg="#EFF6FF"
          iconColor="#3B82F6"
        />
        <MetricCard
          label="Consignadores activos"
          value={summary ? String(summary.activeConsignors) : '—'}
          icon={<TaskAltIcon />}
          iconBg="#F0FDF4"
          iconColor="#22C55E"
        />
        <MetricCard
          label="Pares en inventario activo"
          value={summary ? `${summary.activeInStockPairs.toLocaleString('es-MX')}` : '—'}
          icon={<InventoryIcon />}
          iconBg="#FFF7ED"
          iconColor="#F97316"
          sub="pares activos"
        />
        <MetricCard
          label="Saldo pendiente de pago"
          value={summary ? moneyMx(summary.pendingBalance) : '—'}
          icon={<WalletIcon />}
          iconBg="#FEE2E2"
          iconColor="#DC2626"
        />
      </Box>

      {/* ── Table card ── */}
      <Paper
        elevation={0}
        sx={{
          border: CARD_BORDER,
          borderColor: CARD_BORDER_COLOR,
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        {/* Search bar */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: CARD_BORDER,
            borderColor: CARD_BORDER_COLOR,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <TextField
            size="small"
            placeholder="Buscar por nombre…"
            value={searchInput}
            onChange={handleSearchChange}
            sx={{ width: { xs: '100%', sm: 320 }, bgcolor: 'white' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                </InputAdornment>
              ),
              endAdornment: searchInput && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearchInput('');
                      setSearch('');
                      setPage(0);
                    }}
                    edge="end"
                    aria-label="Limpiar búsqueda"
                    sx={{
                      color: 'text.disabled',
                      '&:hover': { color: 'text.primary' },
                    }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Typography variant="caption" color="text.secondary">
            Mostrando {rangeLabel} de {total} consignadores
          </Typography>
        </Box>

        {loadError && (
          <Alert severity="warning" sx={{ m: 2, mb: 0 }}>
            {loadError}
          </Alert>
        )}

        {/* Table */}
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 800 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: TABLE_HEADER_BG }}>
                {/* ID */}
                <TableCell
                  sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', minWidth: 20 }}
                >
                  <TableSortLabel
                    active={orderBy === 'id'}
                    direction={orderBy === 'id' ? (orderDirection ?? 'asc') : 'asc'}
                    onClick={() => handleSort('id')}
                    sx={sortLabelSx}
                  >
                    ID
                  </TableSortLabel>
                </TableCell>
                {/* Consignor */}
                <TableCell
                  sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', minWidth: 240 }}
                >
                  <TableSortLabel
                    active={orderBy === 'name'}
                    direction={orderBy === 'name' ? (orderDirection ?? 'asc') : 'asc'}
                    onClick={() => handleSort('name')}
                    sx={sortLabelSx}
                  >
                    Consignador
                  </TableSortLabel>
                </TableCell>
                {/* isActive */}
                <TableCell
                  sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', width: 110 }}
                >
                  Estatus
                </TableCell>
                {/* Commission */}
                <TableCell
                  align="right"
                  sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', width: 110 }}
                >
                  Comisión
                </TableCell>
                {/* In-stock pairs */}
                <TableCell
                  align="right"
                  sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', width: 140 }}
                >
                  <TableSortLabel
                    active={orderBy === 'inStockPairs'}
                    direction={orderBy === 'inStockPairs' ? (orderDirection ?? 'desc') : 'desc'}
                    onClick={() => handleSort('inStockPairs')}
                    sx={sortLabelSx}
                  />
                  Inventario
                </TableCell>
                {/* Lifetime sales */}
                <TableCell
                  align="right"
                  sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', width: 180 }}
                >
                  <TableSortLabel
                    active={orderBy === 'lifetimeSales'}
                    direction={orderBy === 'lifetimeSales' ? (orderDirection ?? 'desc') : 'desc'}
                    onClick={() => handleSort('lifetimeSales')}
                    sx={sortLabelSx}
                  >
                    Ventas totales
                  </TableSortLabel>
                </TableCell>
                {/* Current balance */}
                <TableCell
                  align="right"
                  sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', width: 180 }}
                >
                  <TableSortLabel
                    active={orderBy === 'currentBalance'}
                    direction={orderBy === 'currentBalance' ? (orderDirection ?? 'desc') : 'desc'}
                    onClick={() => handleSort('currentBalance')}
                    sx={sortLabelSx}
                  >
                    Saldo actual
                  </TableSortLabel>
                </TableCell>
                {/* Arrow */}
                <TableCell sx={{ width: 48 }} />
              </TableRow>
            </TableHead>

            <TableBody>
              {!loading &&
                rows.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    onClick={() => handleRowClick(row.id)}
                    sx={{
                      cursor: 'pointer',
                      '&:last-child td': { borderBottom: 0 },
                      '& .row-action': { opacity: 0, transition: 'opacity 0.15s' },
                      '&:hover .row-action': { opacity: 1 },
                    }}
                  >
                    {/* ID */}
                    <TableCell sx={{ py: 1.75 }}>
                      <Typography variant="body2" fontWeight={600} color="text.secondary">
                        {row.id}
                      </Typography>
                    </TableCell>

                    {/* Consignor cell */}
                    <TableCell sx={{ py: 1.75 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <ConsignorAvatar name={row.name} />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            fontWeight={700}
                            sx={{
                              maxWidth: 220,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {row.name || 'Sin nombre'}
                          </Typography>
                          {row.isIncomplete && (
                            <Chip
                              label="Registro pendiente"
                              size="small"
                              sx={{
                                mt: 0.35,
                                height: 20,
                                fontSize: '0.6875rem',
                                fontWeight: 700,
                                bgcolor: 'rgba(245, 158, 11, 0.16)',
                                color: 'rgb(180, 83, 9)',
                                '& .MuiChip-label': { px: 0.75 },
                              }}
                            />
                          )}
                          <Typography
                            variant="caption"
                            color="text.disabled"
                            sx={{ display: 'block', mt: 0.15 }}
                            noWrap
                          >
                            {row.email || '—'}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    {/* isActive */}
                    <TableCell sx={{ py: 1.75 }}>
                      <InventoryStatusChip status={row.isActive ? 'Activo' : 'Inactivo'} />
                    </TableCell>

                    {/* Commission */}
                    <TableCell align="right" sx={{ py: 1.75 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {row.commission}%
                      </Typography>
                    </TableCell>

                    {/* In-stock pairs */}
                    <TableCell align="right" sx={{ py: 1.75 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {row.inStockPairs.toLocaleString('es-MX')}
                      </Typography>
                      <Typography variant="caption" color="text.disabled">
                        pares
                      </Typography>
                    </TableCell>

                    {/* Lifetime sales */}
                    <TableCell align="right" sx={{ py: 1.75 }}>
                      <Typography variant="body2" fontWeight={600} color="text.secondary">
                        {moneyMx(row.lifetimeSales)}
                      </Typography>
                    </TableCell>

                    {/* Current balance */}
                    <TableCell align="right" sx={{ py: 1.75 }}>
                      <Tooltip
                        title={row.currentBalance > 0 ? 'Pago pendiente al consignador' : ''}
                        placement="left"
                      >
                        <Typography
                          variant="body2"
                          fontWeight={800}
                          sx={{
                            color: row.currentBalance > 0 ? '#1D4ED8' : 'text.secondary',
                          }}
                        >
                          {moneyMx(row.currentBalance)}
                        </Typography>
                      </Tooltip>
                    </TableCell>

                    {/* Actions */}
                    <TableCell align="right" sx={{ py: 1.75, pr: 1.5 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                          gap: 0.75,
                        }}
                      >
                        {row.isIncomplete && (
                          <Tooltip title="Enviar de nuevo el WhatsApp para terminar el registro">
                            <span>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={
                                  resendingId === row.id ? (
                                    <CircularProgress size={14} color="inherit" />
                                  ) : (
                                    <SendIcon fontSize="small" />
                                  )
                                }
                                disabled={resendingId === row.id}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleResendOnboarding(row.id).catch(() => {});
                                }}
                                sx={{
                                  textTransform: 'none',
                                  fontWeight: 700,
                                  fontSize: 12,
                                  whiteSpace: 'nowrap',
                                  borderColor: 'rgba(245, 158, 11, 0.45)',
                                  color: 'rgb(180, 83, 9)',
                                  '&:hover': {
                                    borderColor: 'rgb(180, 83, 9)',
                                    bgcolor: 'rgba(245, 158, 11, 0.08)',
                                  },
                                }}
                              >
                                Reenviar WhatsApp
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                        <Box
                          className="row-action"
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            gap: 0.5,
                          }}
                        >
                          <Tooltip title="Editar consignador">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditConsignorId(row.id);
                              }}
                              aria-label="Editar consignador"
                              sx={{ color: 'text.disabled', '&:hover': { color: 'text.primary' } }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <ChevronRightIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                        </Box>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}

              {loading && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                    <CircularProgress size={28} sx={{ color: '#111827' }} />
                  </TableCell>
                </TableRow>
              )}

              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <GroupIcon sx={{ fontSize: 40, color: 'grey.300' }} />
                      <Typography color="text.secondary" fontWeight={600}>
                        Sin consignadores
                      </Typography>
                      {search && (
                        <Typography variant="body2" color="text.disabled">
                          Ningún consignador coincide con &ldquo;{search}&rdquo;.
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={pageSize}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Filas por página"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}–${to} de ${count !== -1 ? count : `más de ${to}`}`
          }
        />
      </Paper>

      <AdminEditConsignorModal
        open={editConsignorId != null}
        consignorId={editConsignorId}
        onClose={() => setEditConsignorId(null)}
        onSaved={() => {
          setEditConsignorId(null);
          load().catch(() => {});
        }}
        onError={(message) => setSnack({ ok: false, message })}
      />

      <Snackbar
        open={Boolean(snack)}
        autoHideDuration={5000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnack(null)}
          severity={snack?.ok ? 'success' : 'error'}
          variant="filled"
          sx={{ fontWeight: 600 }}
        >
          {snack?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
